import { ColumnDef } from "@tanstack/react-table";
import dayjs from "dayjs";

import { toCsv } from "../../../utils/csv";
import { saveBlobAs } from "../../../utils/fileDownload";

/**
 * A column def, narrowed to the two accessor shapes TanStack allows. The
 * public `ColumnDef` union hides them behind display/group variants, and a
 * cast is cheaper than walking the union for two optional properties.
 */
interface IAccessors<T> {
  accessorFn?: (row: T, index: number) => unknown;
  accessorKey?: string;
}

interface ICsvField<T> {
  header: string;
  read: (row: T, index: number) => unknown;
}

/** Reads a possibly dotted `accessorKey` path off a row. */
function readPath(row: unknown, path: string): unknown {
  return path
    .split(".")
    .reduce<unknown>(
      (value, key) =>
        value === null || value === undefined
          ? undefined
          : (value as Record<string, unknown>)[key],
      row
    );
}

/**
 * The exportable fields of a table, derived from the column defs it already
 * has — so a page describes its columns once and the export follows.
 *
 * Two kinds of column are skipped, both deliberately:
 *
 * - **no header text.** Action columns declare `header: ""`; a column of
 *   buttons has nothing to write to a file.
 * - **no accessor.** The value comes from `accessorFn`/`accessorKey`, never
 *   from the `cell` renderer, which returns React elements — and, for a
 *   column like Value in the translations grid, an editable control rather
 *   than a value at all.
 */
export function csvFieldsFromColumns<T>(
  columns: ColumnDef<T, any>[]
): ICsvField<T>[] {
  return columns.flatMap((column) => {
    const header =
      typeof column.header === "string" ? column.header.trim() : "";
    if (!header) return [];

    const { accessorFn, accessorKey } = column as IAccessors<T>;
    if (accessorFn) return [{ header, read: accessorFn }];
    if (accessorKey) {
      return [{ header, read: (row: T) => readPath(row, accessorKey) }];
    }
    return [];
  });
}

export interface ITableCsvExport<T> {
  /** Base of the saved filename; the date and extension are added. */
  filename: string;
  columns: ColumnDef<T, any>[];
  /** The rows to write — the whole filtered set, not just the page shown. */
  rows: T[];
  /**
   * True for a server-paginated table, where only the fetched page is in
   * memory: the export is labelled "current page" so nobody mistakes it for
   * the whole list.
   */
  currentPageOnly?: boolean;
}

/** "Export 12 rows" / "Export this page (12 rows)" — the export control's label. */
export function exportCsvLabel<T>({
  rows,
  currentPageOnly,
}: ITableCsvExport<T>): string {
  const count = `${rows.length} row${rows.length === 1 ? "" : "s"}`;
  return currentPageOnly
    ? `Export this page as CSV (${count})`
    : `Export ${count} as CSV`;
}

/** Builds the CSV for a table and hands it to the browser's downloader. */
export function exportTableCsv<T>({
  filename,
  columns,
  rows,
}: ITableCsvExport<T>): void {
  const fields = csvFieldsFromColumns(columns);
  const text = toCsv(
    fields.map((field) => field.header),
    rows.map((row, index) => fields.map((field) => field.read(row, index)))
  );

  saveBlobAs(
    new Blob([text], { type: "text/csv;charset=utf-8;" }),
    `${filename}-${dayjs().format("YYYY-MM-DD")}.csv`
  );
}
